import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Plus, Edit, Trash2, Check, X, Loader2, 
  Scale, AlertCircle, CheckCircle2, Clock, Ban
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { toast } from 'sonner';
import { auditoriaExpedientesAPI } from '../api';

// Mapeo de colores por estado
const ESTADO_COLORS = {
  pendiente: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
  en_tramite: { bg: 'bg-blue-100', text: 'text-blue-700', icon: AlertCircle },
  resuelto_favorable: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2 },
  resuelto_desfavorable: { bg: 'bg-red-100', text: 'text-red-700', icon: Ban },
  no_aplica: { bg: 'bg-gray-100', text: 'text-gray-500', icon: X },
};

export default function ClausulasSection({ expedienteId }) {
  const [loading, setLoading] = useState(true);
  const [clausulas, setClausulas] = useState([]);
  const [tiposClausula, setTiposClausula] = useState([]);
  const [estadosClausula, setEstadosClausula] = useState([]);
  
  // Estado para modal
  const [showModal, setShowModal] = useState(false);
  const [editingClausula, setEditingClausula] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    tipo: '',
    estado: 'pendiente',
    importe_reclamado: '',
    importe_recuperado: '',
    observaciones: ''
  });
  
  // Cargar datos
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [clausulasRes, tiposRes, estadosRes] = await Promise.all([
        auditoriaExpedientesAPI.getClausulas(expedienteId),
        auditoriaExpedientesAPI.getTiposClausulas(),
        auditoriaExpedientesAPI.getEstadosClausulas()
      ]);
      
      setClausulas(clausulasRes.data);
      setTiposClausula(tiposRes.data);
      setEstadosClausula(estadosRes.data);
    } catch (error) {
      console.error('Error cargando cláusulas:', error);
      toast.error('Error al cargar las cláusulas');
    } finally {
      setLoading(false);
    }
  }, [expedienteId]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Abrir modal para crear nueva cláusula
  const handleCreate = () => {
    setEditingClausula(null);
    setFormData({
      tipo: '',
      estado: 'pendiente',
      importe_reclamado: '',
      importe_recuperado: '',
      observaciones: ''
    });
    setShowModal(true);
  };
  
  // Abrir modal para editar cláusula
  const handleEdit = (clausula) => {
    setEditingClausula(clausula);
    setFormData({
      tipo: clausula.tipo,
      estado: clausula.estado,
      importe_reclamado: clausula.importe_reclamado || '',
      importe_recuperado: clausula.importe_recuperado || '',
      observaciones: clausula.observaciones || ''
    });
    setShowModal(true);
  };
  
  // Guardar cláusula
  const handleSave = async () => {
    if (!formData.tipo) {
      toast.error('Selecciona un tipo de cláusula');
      return;
    }
    
    try {
      setSaving(true);
      
      const data = {
        tipo: formData.tipo,
        estado: formData.estado,
        importe_reclamado: formData.importe_reclamado ? parseFloat(formData.importe_reclamado) : null,
        importe_recuperado: formData.importe_recuperado ? parseFloat(formData.importe_recuperado) : null,
        observaciones: formData.observaciones || null
      };
      
      if (editingClausula) {
        await auditoriaExpedientesAPI.updateClausula(expedienteId, editingClausula.id, data);
        toast.success('Cláusula actualizada');
      } else {
        await auditoriaExpedientesAPI.createClausula(expedienteId, data);
        toast.success('Cláusula añadida');
      }
      
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Error guardando cláusula:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };
  
  // Eliminar cláusula
  const handleDelete = async (clausulaId) => {
    if (!window.confirm('¿Eliminar esta cláusula?')) return;
    
    try {
      await auditoriaExpedientesAPI.deleteClausula(expedienteId, clausulaId);
      toast.success('Cláusula eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };
  
  // Formatear moneda
  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };
  
  // Obtener tipos disponibles (no usados)
  const getTiposDisponibles = () => {
    const tiposUsados = clausulas.map(c => c.tipo);
    if (editingClausula) {
      // Si estamos editando, incluir el tipo actual
      return tiposClausula;
    }
    return tiposClausula.filter(t => !tiposUsados.includes(t.value));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Scale className="h-5 w-5" />
            Cláusulas Reclamadas
          </CardTitle>
          <Button size="sm" onClick={handleCreate} disabled={getTiposDisponibles().length === 0}>
            <Plus className="h-4 w-4 mr-1" />
            Añadir
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {clausulas.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <FileText className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p>No hay cláusulas registradas</p>
            <p className="text-sm">Añade las cláusulas que se reclaman en este expediente</p>
          </div>
        ) : (
          <div className="space-y-2">
            {clausulas.map(clausula => {
              const estadoInfo = ESTADO_COLORS[clausula.estado] || ESTADO_COLORS.pendiente;
              const IconEstado = estadoInfo.icon;
              
              return (
                <div 
                  key={clausula.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${estadoInfo.bg}`}>
                      <IconEstado className={`h-4 w-4 ${estadoInfo.text}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{clausula.tipo_display}</span>
                        <Badge variant="outline" className="text-xs">
                          {clausula.tipo}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <Badge className={`${estadoInfo.bg} ${estadoInfo.text} border-0`}>
                          {clausula.estado_display}
                        </Badge>
                        {clausula.importe_reclamado && (
                          <span>Reclamado: {formatCurrency(clausula.importe_reclamado)}</span>
                        )}
                        {clausula.importe_recuperado && (
                          <span className="text-green-600">
                            Recuperado: {formatCurrency(clausula.importe_recuperado)}
                          </span>
                        )}
                      </div>
                      {clausula.observaciones && (
                        <p className="text-xs text-gray-500 mt-1">{clausula.observaciones}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(clausula)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700"
                      onClick={() => handleDelete(clausula.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Resumen */}
        {clausulas.length > 0 && (
          <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Total reclamado</p>
              <p className="font-bold">
                {formatCurrency(clausulas.reduce((sum, c) => sum + (c.importe_reclamado || 0), 0))}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Total recuperado</p>
              <p className="font-bold text-green-600">
                {formatCurrency(clausulas.reduce((sum, c) => sum + (c.importe_recuperado || 0), 0))}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Pendiente</p>
              <p className="font-bold text-orange-600">
                {formatCurrency(
                  clausulas.reduce((sum, c) => sum + (c.importe_reclamado || 0), 0) -
                  clausulas.reduce((sum, c) => sum + (c.importe_recuperado || 0), 0)
                )}
              </p>
            </div>
          </div>
        )}
      </CardContent>
      
      {/* Modal para crear/editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingClausula ? 'Editar Cláusula' : 'Nueva Cláusula'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Tipo de cláusula</Label>
              <Select
                value={formData.tipo}
                onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                disabled={!!editingClausula}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {(editingClausula ? tiposClausula : getTiposDisponibles()).map(tipo => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label} ({tipo.value})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Estado</Label>
              <Select
                value={formData.estado}
                onValueChange={(value) => setFormData({ ...formData, estado: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar estado..." />
                </SelectTrigger>
                <SelectContent>
                  {estadosClausula.map(estado => (
                    <SelectItem key={estado.value} value={estado.value}>
                      {estado.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Importe reclamado (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.importe_reclamado}
                  onChange={(e) => setFormData({ ...formData, importe_reclamado: e.target.value })}
                />
              </div>
              <div>
                <Label>Importe recuperado (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.importe_recuperado}
                  onChange={(e) => setFormData({ ...formData, importe_recuperado: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Observaciones</Label>
              <Textarea
                placeholder="Notas adicionales..."
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {editingClausula ? 'Actualizar' : 'Guardar'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
